import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ScrollToTop from '../components/ScrollToTop';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Project {
  title: string;
  description: string;
  image: string;
  quantity: string;
  completedYear: string;
  gallery?: string[];
}

const projects: Project[] = [
 {
    title: "Headquarters of an International Organization (Old Wan Chai Police Station)",
    description: "R/f, 1-3/F, Renovation Works for Headquarters of an International Organization",
    image: "/assets/images/projects_old_wanchi_police.jpg",
    quantity: "2200 sqm",
    completedYear: "2024-Ongoing",
    gallery: [
       "/assets/images/old_wanchi_police_02.jpg",
      "/assets/images/old_wanchi_police_03.jpg",
       "/assets/images/old_wanchi_police_01.jpg",
       "/assets/images/old_wanchi_police_05.jpg",
       "/assets/images/old_wanchi_police_07.jpg",
       "/assets/images/old_wanchi_police_08.jpg"
    ]
  },
  {
    title: "Hong Kong-Shenzhen Innovation and Technology Park",
    description: "Waterproofing on main works contract of batch 1A",
    image: "/assets/images/project_HSITP.jpeg",
    quantity: "40000 sqm",
    completedYear: "2022-Ongoing",
    gallery: [
      "/assets/images/Project_HSITP08.jpg",
      "/assets/images/Project_HSITP06.jpg",
      "/assets/images/Project_HSITP01.jpg",
      "/assets/images/Project_HSITP03.jpg",
      "/assets/images/Project_HSITP07.jpg"
    ]
  },
  {
    title: "New Acute Hospital at Kai Tak Development",
    description: "Advanced waterproofing systems for terminal buildings",
    image: "/assets/images/project_kaitak.jpg",
    quantity: "90,000 sqm",
    completedYear: "2022-Ongoing",
    gallery: [
      "/assets/images/acute_kaitak_010.jpg",
      "/assets/images/acute_kaitak_03.jpg",
      "/assets/images/acute_kaitak_05.jpg",
      "/assets/images/acute_kaitak_09.jpg",
          "/assets/images/acute_kaitak_01.jpg",
          "/assets/images/acute_kaitak_012.jpg"
    ]
  },
  {
    title: "Residential Development at One Stanley",
    description: "Waterproofing on residential development",
    image: "/assets/images/project_stanley.jpg",
    quantity: "76,000 sqm",
    completedYear: "2021-2023",
    gallery: [
      "/assets/images/Onestanley03.jpg",
     "/assets/images/Onestanley01.jpg",
     "/assets/images/Onestanley04.jpg", 
      "/assets/images/Onestanley05.jpg",
       "/assets/images/Onestanley06.jpg"
    ]
  },
  {
    title: "HSBC Building",
    description: "L28, L35, L37, L41, HSBC Terrace Re-roofing Works",
    image: "/assets/images/project_hsbc.jpg",
    quantity: "2,000 sqm",
    completedYear: "2022-Ongoing",
    gallery: [
      "/assets/images/Project_HSBC20F02.jpg",
      "/assets/images/Project_HSBC20F03.jpg",
      "/assets/images/Project_HSBC46F01.jpg",
         "/assets/images/Project_HSBC20F01.jpg",
    ]
  },
  {
    title: "Our Lady of Maryknoll Hospital",
    description: "Waterproofing",
    image: "/assets/images/maryknoll_hospital.jpg",
    quantity: "38,000 sqm",
    completedYear: "2024-Ongoing",
    gallery: [
      "/assets/images/maryknoll_hospital_03.png"
    ]
  },
];

const Project: React.FC = () => {
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openModal = (project: Project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
    document.body.style.overflow = '';
  };

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedProject?.gallery) {
      setCurrentImageIndex((prev) => 
        prev === selectedProject.gallery!.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedProject?.gallery) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? selectedProject.gallery!.length - 1 : prev - 1
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <section className="pt-24 pb-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Featured Projects</h2>
            <div className="w-24 h-1 bg-primary-light mx-auto mb-6"></div>
            <p className="max-w-2xl mx-auto text-xl text-gray-600">
              Hop Shing Engineering & Construction Limited has been involved in a large number of projects within Greater China.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <div 
                key={index}
                className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:bg-primary-light cursor-pointer"
                onClick={() => openModal(project)}
              >
                <div className="p-4">
                  <div className="relative w-full h-48 mb-4 overflow-hidden rounded-lg">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-white transition-colors duration-300">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 mb-3 group-hover:text-white/90 transition-colors duration-300">
                    {project.description}
                  </p>
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500 group-hover:text-white/80 transition-colors duration-300">
                      Quantity: {project.quantity}
                    </p>
                    <p className="text-sm text-gray-500 group-hover:text-white/80 transition-colors duration-300">
                      Project Period: {project.completedYear}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-12">
            <button 
              onClick={() => {
                navigate('/more-projects');
                window.scrollTo(0, 0);
              }}
              className="px-6 py-2 border-2 border-primary-light text-primary-light rounded-md hover:bg-primary-light hover:text-white transition-all duration-300"
            >
              More Projects
            </button>
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedProject && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 overflow-y-auto"
          onClick={closeModal}
        >
          <div 
            className="min-h-screen px-4 py-32 md:py-40"
          >
            <div
              className="relative w-full max-w-3xl mx-auto bg-transparent rounded-lg overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="relative">
                <img
                  src={selectedProject.gallery?.[currentImageIndex]}
                  alt={`${selectedProject.title} - Image ${currentImageIndex + 1}`}
                  className="w-full h-auto max-h-[60vh] object-contain mx-auto"
                />

                {selectedProject.gallery && selectedProject.gallery.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/40 p-2 rounded-full backdrop-blur-sm transition-all duration-300"
                      aria-label="Previous image"
                    >
                      <ChevronLeft size={24} className="text-white" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/40 p-2 rounded-full backdrop-blur-sm transition-all duration-300"
                      aria-label="Next image"
                    >
                      <ChevronRight size={24} className="text-white" />
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              {selectedProject.gallery && selectedProject.gallery.length > 1 && (
                <div className="flex justify-center gap-2 mt-4 px-4 pb-2 overflow-x-auto">
                  {selectedProject.gallery.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`relative flex-shrink-0 transition-all duration-300 ${
                        currentImageIndex === index 
                          ? 'ring-2 ring-primary-light scale-105' 
                          : 'ring-2 ring-transparent opacity-50 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-16 h-16 object-cover rounded-md"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ScrollToTop />
      <Footer />
    </div>
  );
};

export default Project;